import React, { memo, useEffect, useState, useRef } from 'react'
//@ts-ignore
import GaugeChart from 'react-gauge-chart'
import { IconInfoCircle } from '@tabler/icons-react'
import { Tooltip } from 'react-tooltip'

interface AIReadinessGaugeProps {
  score: number
  forceRerender?: number
}

const AIReadinessGauge: React.FC<AIReadinessGaugeProps> = memo(({ score, forceRerender = 0 }) => {
  const [chartKey, setChartKey] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const resizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    // Force chart to re-render after a short delay to allow container to settle
    const timer = setTimeout(() => {
      setChartKey((prev) => prev + 1)
    }, 350) // Slightly longer than CSS transition duration

    return () => clearTimeout(timer)
  }, [forceRerender])

  useEffect(() => {
    // Use ResizeObserver to detect container size changes
    if (!containerRef.current) return

    const resizeObserver = new ResizeObserver(() => {
      // Debounce the resize event
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
      resizeTimerRef.current = setTimeout(() => {
        setChartKey((prev) => prev + 1)
        resizeTimerRef.current = null
      }, 100)
    })

    resizeObserver.observe(containerRef.current)

    return () => {
      resizeObserver.disconnect()
      if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current)
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={`ai-readiness-gauge p-1 rounded-[5px] shadow-md m-2`}
      data-testid="ai-readiness-gauge">
      <div className="flex items-center mb-2">
        <span
          className="me-50 text-subheading font-semibold text-lg"
          data-testid="ai-readiness-gauge-title">
          AI Readiness Score
        </span>
        <IconInfoCircle
          size={16}
          className="text-subheading cursor-pointer"
          data-tooltip-id="ai-score"
          data-testid="ai-readiness-gauge-info"
        />
        <Tooltip
          id="ai-score"
          place="bottom"
          className="!z-[9999] rounded-lg px-4 py-2 max-w-xs text-center">
          AI Readiness score assesses how well prepared a dataset is for AI
        </Tooltip>
      </div>
      <div className="flex items-center justify-center">
        <h3 className="text-2xl font-bold ml-2" data-testid="ai-readiness-gauge-score">
          {score < 33 ? 'Low' : score < 66 ? 'Medium' : 'High'}
        </h3>
        <div className="w-3/4">
          <GaugeChart
            key={`gauge-${chartKey}`}
            id={`gauge-chart-${chartKey}`}
            arcsLength={[0.33, 0.33, 0.33]}
            colors={['#EA4228', '#F5CD19', '#5BE12C']}
            percent={score / 100}
            arcPadding={0.02}
            cornerRadius={0}
            textColor="var(--text-heading, #000000)"
            needleColor="var(--gauge-needle)"
            needleBaseColor="var(--gauge-needle)"
            backgroundColor="var(--panel-surface-bg, #f0f0f0)"
          />
        </div>
      </div>
    </div>
  )
})

export default AIReadinessGauge
